import dayjs from "dayjs";
import { getToken } from "../auth/token";

// PORT OF `src/app/_services/floor-sections.service.ts` (ISSUES #71 AND #98): THE
// FLOOR SECTION(S) A HOST IS WORKING, CAPTURED AT SIGN-IN AND SAVED TO THE
// CORE-API `active_users` RECORD, WHICH SECTION-SCOPED NOTIFICATIONS (#70) READ.
//
// THE API HAS NO PER-USER READ PATH (`/crm/get-active-users` IS MANAGER-ONLY), SO
// WHETHER TO ASK AGAIN IS DECIDED FROM A LOCAL, DAY-STAMPED, SESSION-BOUND ECHO OF
// THE LAST SUCCESSFUL SAVE. SAME KEY AND SHAPE AS ANGULAR, SO BOTH APPS ON ONE
// ORIGIN AGREE ON WHETHER THE HOST HAS ALREADY ANSWERED TODAY.
export const FLOOR_SECTIONS_KEY = "HOST_FLOOR_SECTIONS";

// `/ht/paged-egms` DEFAULTS TO 10 ROWS; THE SECTION LIST NEEDS THE WHOLE FLOOR
export const EGM_PAGE_SIZE = 10_000;

interface StoredFloorSections {
  userId: number;
  day: string;
  session: string;
  sections: string[];
}

const today = () => dayjs().format("YYYY-MM-DD");

// THE LAST 16 CHARACTERS OF THE TOKEN SIGNATURE: CHANGES ON EVERY SIGN-IN AND
// NEEDS NO CLAIM PARSING. A NEW SESSION RE-ASKS EVEN ON THE SAME DAY - AN EXPIRED
// TOKEN AND A FRESH `?t=` HANDOFF BOTH START ONE WITHOUT A LOGOUT.
export function sessionFingerprint(token: string | null = getToken()): string {
  return (token ?? "").split(".")[2]?.slice(-16) ?? "";
}

function readStore(): StoredFloorSections | null {
  try {
    const raw = localStorage.getItem(FLOOR_SECTIONS_KEY);
    return raw ? (JSON.parse(raw) as StoredFloorSections) : null;
  } catch {
    return null;
  }
}

// THE SECTIONS SAVED FOR THIS USER, TODAY, IN THIS SESSION - OR NONE
export function storedSections(userId: number): string[] {
  const stored = readStore();
  const session = sessionFingerprint();
  if (!stored || stored.userId !== userId || stored.day !== today()) return [];
  if (!session || stored.session !== session) return [];
  return Array.isArray(stored.sections) ? stored.sections : [];
}

export const needsSelection = (userId: number) => storedSections(userId).length === 0;

export function storeSections(userId: number, sections: string[]): void {
  try {
    localStorage.setItem(FLOOR_SECTIONS_KEY, JSON.stringify({ userId, day: today(), session: sessionFingerprint(), sections }));
  } catch {
    // STORAGE UNAVAILABLE - THE HOST IS SIMPLY ASKED AGAIN NEXT LOAD
  }
}

// THE SESSION IN WHICH THE HOST LEFT AN UNANSWERABLE PROMPT. MODULE STATE, NOT
// PERSISTED: A RELOAD ASKS AGAIN, SO ONE DISMISSAL NEVER LEAVES A HOST UNSCOPED
// FOR A WHOLE DAY. KEYED BY SESSION SO THE NEXT HOST IN THIS TAB DOES NOT INHERIT IT.
let skippedSession: string | null = null;

export function markSkippedThisSession(): void {
  skippedSession = sessionFingerprint();
}

export function wasSkippedThisSession(): boolean {
  const session = sessionFingerprint();
  return !!session && skippedSession === session;
}

// ON SIGN-OUT: THE NEXT USER OF THIS BROWSER MUST NOT INHERIT THE ECHO OR THE SKIP.
// THE SERVER ROW IS NOT TORN DOWN (THE PRM NEVER CALLS `/api/logout`).
export function clearStoredSections(): void {
  skippedSession = null;
  try {
    localStorage.removeItem(FLOOR_SECTIONS_KEY);
  } catch {
    // NOTHING TO DO
  }
}

// THE SELECTABLE SECTIONS, DE-DUPLICATED FROM THE PER-MACHINE `sectionName` - THE
// API HAS NO SECTIONS TABLE. A MISSING ROWS ARRAY IS SHAPE DRIFT, NOT AN EMPTY
// FLOOR, AND MUST NOT BE READ AS "NOTHING TO ASK": IT THROWS, SO THE PROMPT OPENS
// WITH RETRY AND A WAY OUT INSTEAD OF SILENTLY LEAVING THE HOST UNSCOPED.
export function sectionsFromEgmPage(data: unknown): string[] {
  const rows = (data as { rows?: unknown } | null | undefined)?.rows;
  if (!Array.isArray(rows)) throw new Error("paged-egms returned no rows array");
  const sections = new Set<string>();
  for (const row of rows) {
    const name = typeof (row as { sectionName?: unknown })?.sectionName === "string" ? (row as { sectionName: string }).sectionName.trim() : "";
    if (name) sections.add(name);
  }
  return [...sections].sort((a, b) => a.localeCompare(b));
}

// `floor_sections` IS A PLAIN TEXT COLUMN; COMMA-SEPARATED IS THE CONTRACT #70 SPLITS ON
export const serializeSections = (sections: string[]) => sections.join(",");

// PORT OF `FloorSectionsService.Parse`: THE READER FOR THE SAME COMMA CONTRACT
export const parseSections = (value: string): string[] =>
  (value || "")
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

// THE SAVE BODY IS SNAKE_CASE, UNLIKE THE REST OF `/crm/` - THE HANDLER PARSES
// `ActiveUserDto` DIRECTLY. `user_id` IS FORCED TO THE TOKEN'S USER SERVER-SIDE.
export const saveBody = (userId: number, sections: string[]) => ({ user_id: userId, floor_sections: serializeSections(sections) });

// THE ENDPOINT REPORTS FAILURE IN THE ENVELOPE; ONLY `status: 200` IS A SAVE
export const saveSucceeded = (resp: unknown) => (resp as { status?: unknown } | null)?.status === 200;
